import React from "react";
import { useSelector } from "react-redux";
import { formValueSelector, reduxForm } from "redux-form";
import { ACTION_DISPLAY_TYPES, FIELD_TYPES } from "../../constants/enums";
import { COMMON, PAGES } from "../../constants/localization";
import ShieldTick from "../../assets/images/icons/rebrand/shield-tick.svg";
import i18n from "../../i18n/config";

// types
import { RootState } from "../../types/redux";
import {
  SecurityQuestionCardOwnProps,
  SecurityQuestionCardProps,
  SecurityQuestionFormValues,
} from "../../types/components/SecurityQuestion";

// ui components 
import Card from "../Common/Card";
import Action from "../Common/Action";
import ActionLink from "../Common/ActionLink";
import ErrorBlock from "../Common/ErrorBlock";
import DynamicInputField from "../Common/DynamicFields/DynamicInputField";

const FORM_NAME = "authSecurityQuestion";
const ANSWER_MAX_LENGTH = 60;

const answerSelector = formValueSelector(FORM_NAME);

const SecurityQuestionCard = ({
  question,
  dataType,
  errors,
  valid,
  submitting,
  handleSubmit,
  onDoNotRememberClick,
}: SecurityQuestionCardProps) => {
  const fieldType = FIELD_TYPES[dataType.toUpperCase() as keyof typeof FIELD_TYPES];
  const isDateAnswer = fieldType === FIELD_TYPES.DATE;

  const answer = useSelector((state: RootState) =>
    answerSelector(state, "answer")
  );
  const answerLength = typeof answer === "string" ? answer.length : 0;

  return (
    <Card
      icon={<img src={ShieldTick} alt="Shield Tick" />}
      title={i18n.t(PAGES.AUTH.SECURITY_QUESTION.TITLE)}
      titleWeight="strong"
      wrapBody={false}
      dataCy="security-question-card"
    >
      <form
        onSubmit={handleSubmit}
        data-testid="security-question-form"
        className="v2"
      >
        <Card.Body>
          {errors && <ErrorBlock filterErrors={false} errors={errors} />}

          <label htmlFor="answer" className="auth-label bold mb-16">
            {question}
          </label>
          <DynamicInputField
            type={fieldType}
            id="answer"
            max={ANSWER_MAX_LENGTH}
            className="form-group"
            hideCounter={true}
            required
          />

          <div className="flex-row justify-content-between">
            <ActionLink
              clickFunc={onDoNotRememberClick}
              text={PAGES.AUTH.SECURITY_QUESTION.DO_NOT_REMEMBER}
              classes="bold text-blue-700"
            />
            {!isDateAnswer && (
              <span className="text-primary bold" aria-hidden="true">
                {answerLength}/{ANSWER_MAX_LENGTH}
              </span>
            )}
          </div>
        </Card.Body>

        <Card.Footer stretch>
          <Action
            disabled={!valid}
            title={COMMON.SEND}
            displayType={ACTION_DISPLAY_TYPES.PRIMARY}
            loading={submitting}
            type="submit"
            dataCy="security-question-submit"
            className="btn-md"
          />
        </Card.Footer>
      </form>
    </Card>
  );
};

export default reduxForm<
  SecurityQuestionFormValues,
  SecurityQuestionCardOwnProps
>({
  form: FORM_NAME,
})(SecurityQuestionCard);